import { useCallback, useRef, type MouseEvent } from 'react';

const CONTROL_SELECTOR = 'input, textarea, select, button';
const IGNORED_SELECTOR = `${CONTROL_SELECTOR}, a, [role="listbox"], [role="option"], [role="button"]`;

export function useFieldClickTarget<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const pendingTrigger = useRef<HTMLElement | null>(null);

  const onMouseDown = useCallback((event: MouseEvent<T>) => {
    pendingTrigger.current = null;
    if (event.button !== 0) return;

    const target = event.target as HTMLElement | null;
    if (!ref.current || !target || target.closest(IGNORED_SELECTOR)) return;

    const control = ref.current.querySelector<HTMLElement>(CONTROL_SELECTOR);
    if (!control || (control as HTMLInputElement).disabled) return;

    event.preventDefault();
    control.focus();

    if (control.tagName === 'BUTTON' && control.getAttribute('aria-expanded') !== 'true') {
      pendingTrigger.current = control;
    }
  }, []);

  const onClick = useCallback(() => {
    const trigger = pendingTrigger.current;
    pendingTrigger.current = null;
    trigger?.click();
  }, []);

  return { ref, onMouseDown, onClick };
}
